import type { ActionItem } from '@/api/types';
import { formatDue, formatMoney } from './format';

export type ActionGroup = 'decide' | 'give' | 'pay' | 'sign';

export const groupTitle: Record<ActionGroup, string> = {
  decide: 'Решить',
  give: 'Прислать и передать',
  pay: 'Оплатить',
  sign: 'Подписать',
};

export type ActionView = { key: string; group: ActionGroup; projectId?: string; to: string; title: string; hint: string; due?: string; cta: string };

/** One vocabulary for everything the studio waits on the client for, used on every screen. */
export function describeAction(a: ActionItem): ActionView {
  switch (a.kind) {
    case 'task': {
      const nc = a.task.needsClient!;
      return {
        key: `task-${a.task.id}`,
        group: nc.kind === 'approve' || nc.kind === 'answer' ? 'decide' : 'give',
        projectId: a.task.projectId,
        to: `/p/${a.task.projectId}/tasks/${a.task.id}`,
        title: a.task.title,
        hint: nc.note,
        due: a.task.due && formatDue(a.task.due),
        cta: nc.kind === 'approve' ? 'Посмотреть' : nc.kind === 'answer' ? 'Ответить' : nc.kind === 'access' ? 'Передать' : 'Загрузить',
      };
    }
    case 'reply':
      return { key: `reply-${a.thread.id}`, group: 'decide', projectId: a.thread.projectId, to: `/p/${a.thread.projectId}/talk/${a.thread.id}`, title: a.thread.lastMessage, hint: `Вопрос от ${a.thread.lastAuthor?.name ?? 'команды'}`, cta: 'Ответить' };
    case 'proposal':
      return { key: `kp-${a.proposal.id}`, group: 'decide', to: `/proposals/${a.proposal.id}`, title: `Предложение: ${a.proposal.title}`, hint: `${formatMoney(a.proposal.total)}, действует ${formatDue(a.proposal.validUntil).replace('через', 'ещё')}`, cta: 'Рассмотреть' };
    case 'change':
      if (a.change.status === 'question') return { key: `cq-${a.change.id}`, group: 'decide', projectId: a.change.projectId, to: `/p/${a.change.projectId}/changes`, title: `Вопрос студии: ${a.change.title}`, hint: a.change.question ?? '', cta: 'Ответить' };
      return { key: `cr-${a.change.id}`, group: 'decide', projectId: a.change.projectId, to: `/p/${a.change.projectId}/changes`, title: `Оценка: ${a.change.title}`, hint: `${formatMoney(a.change.estimate!.price)}, ${a.change.estimate!.days} раб. дней`, cta: 'Решить' };
    case 'feedback':
      return { key: `fb-${a.projectId}`, group: 'decide', projectId: a.projectId, to: `/p/${a.projectId}?feedback=1`, title: 'Как вам работа с нами?', hint: a.context, cta: 'Оценить' };
    case 'onboarding':
      if (a.item.scope === 'access') return { key: `ob-${a.item.id}`, group: 'give', projectId: a.item.projectId, to: `/access?project=${a.item.projectId}&title=${encodeURIComponent(a.item.title)}&onboarding=${a.item.id}${a.item.toId ? `&to=${a.item.toId}` : ''}`, title: a.item.title, hint: a.item.hint, cta: 'Передать' };
      return { key: `ob-${a.item.id}`, group: 'give', projectId: a.item.projectId, to: `/p/${a.item.projectId}`, title: a.item.title, hint: a.item.hint, cta: 'Открыть' };
    case 'secret':
      return { key: `sec-${a.secret.id}`, group: 'give', projectId: a.secret.projectId, to: '/access', title: `Доступ для вас: ${a.secret.title}`, hint: `от ${a.secret.from.name}, откроется один раз`, cta: 'Открыть' };
    case 'invoice':
      return { key: `inv-${a.invoice.id}`, group: 'pay', projectId: a.invoice.projectId, to: `/money/${a.invoice.id}?pay=1`, title: `Счёт № ${a.invoice.number} на ${formatMoney(a.invoice.total)}`, hint: a.invoice.lines[0]?.note ?? a.invoice.lines[0]?.title ?? '', due: formatDue(a.invoice.dueAt), cta: 'Оплатить' };
    case 'act':
      return { key: `act-${a.act.id}`, group: 'sign', to: '/money?tab=acts', title: `Акт № ${a.act.number}`, hint: `на ${formatMoney(a.act.total)}, счёт уже оплачен`, cta: 'Подписать' };
    case 'sign':
      return { key: `sg-${a.sign.id}`, group: 'sign', projectId: a.sign.projectId, to: '/documents?tab=sign', title: a.sign.title, hint: 'Простая электронная подпись в кабинете', cta: 'Подписать' };
    case 'payer':
      return {
        key: `payer-${a.payer.id}`,
        group: 'give',
        to: `/settings?payer=${a.payer.id}`,
        title: a.text,
        hint: `Нужно для счетов по ${a.payer.projects.length > 1 ? 'проектам' : 'проекту'} ${a.payer.projects.map((p) => `«${p.title}»`).join(', ')}`,
        cta: 'Дополнить',
      };
  }
}
